import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import "./TagSuggestionsModal.css";

interface TagSuggestion {
  tag: string;
  reason?: string;
}

interface TagSuggestionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  suggestions?: TagSuggestion[];
  onAddTags: (tags: string[]) => void;
  isLoading?: boolean;
}

const TagSuggestionsModal: React.FC<TagSuggestionsModalProps> = ({
  isOpen,
  onClose,
  suggestions,
  onAddTags,
  isLoading,
}) => {
  const { t } = useTranslation(["recruiting", "common"]);
  const [selectedTags, setSelectedTags] = useState<Set<string>>(new Set());

  // Reset selections when modal opens with new suggestions
  useEffect(() => {
    if (isOpen) {
      setSelectedTags(new Set());
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleToggleTag = (tag: string) => {
    setSelectedTags((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(tag)) {
        newSet.delete(tag);
      } else {
        newSet.add(tag);
      }
      return newSet;
    });
  };

  const handleAddSelected = () => {
    if (selectedTags.size > 0) {
      onAddTags(Array.from(selectedTags));
      setSelectedTags(new Set());
      onClose();
    }
  };

  const handleSelectAll = () => {
    if (suggestions && suggestions.length > 0) {
      setSelectedTags(new Set(suggestions.map((s) => s.tag)));
    }
  };

  const handleDeselectAll = () => {
    setSelectedTags(new Set());
  };

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="tag-suggestions-loading">
          <div className="spinner"></div>
          <p>{t("recruiting:tags.generating")}</p>
        </div>
      );
    }

    if (suggestions && suggestions.length > 0) {
      return (
        <>
          <div className="tag-suggestions-actions">
            <button
              type="button"
              onClick={handleSelectAll}
              className="btn btn-sm btn-outline"
            >
              {t("common:actions.selectAll")}
            </button>
            <button
              type="button"
              onClick={handleDeselectAll}
              className="btn btn-sm btn-outline"
            >
              {t("recruiting:tags.deselectAll")}
            </button>
            <span className="tag-suggestions-count">
              {t("recruiting:tags.selectedCount", {
                selected: selectedTags.size,
                total: suggestions.length,
              })}
            </span>
          </div>

          <div className="tag-suggestions-list">
            {suggestions.map((suggestion) => (
              <label
                key={`tag-${suggestion.tag}`}
                className={`tag-suggestion-item ${selectedTags.has(suggestion.tag) ? "selected" : ""}`}
              >
                <div className="tag-suggestion-checkbox">
                  <input
                    type="checkbox"
                    checked={selectedTags.has(suggestion.tag)}
                    onChange={() => handleToggleTag(suggestion.tag)}
                    aria-label={`${t("recruiting:tags.selectTag")}: ${suggestion.tag}`}
                  />
                </div>
                <div className="tag-suggestion-content">
                  <span className="tag-suggestion-tag">{suggestion.tag}</span>
                  <span className="tag-suggestion-reason">
                    {suggestion.reason}
                  </span>
                </div>
              </label>
            ))}
          </div>
        </>
      );
    }

    return (
      <div className="tag-suggestions-empty">
        <p>{t("recruiting:tags.noSuggestions")}</p>
      </div>
    );
  };

  return createPortal(
    <div
      className="tag-suggestions-overlay"
      onClick={onClose}
      onKeyDown={(e) => e.key === "Escape" && onClose()}
      role="presentation"
    >
      <dialog
        className="tag-suggestions-modal"
        open
        aria-labelledby="tag-modal-title"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="tag-suggestions-header">
          <h3 id="tag-modal-title">
            {t("recruiting:tags.aiSuggestionsTitle")}
          </h3>
          <button className="tag-suggestions-close" onClick={onClose}>
            &times;
          </button>
        </div>

        <div className="tag-suggestions-content">{renderContent()}</div>

        <div className="tag-suggestions-footer">
          <button type="button" onClick={onClose} className="btn btn-secondary">
            {t("common:actions.cancel")}
          </button>
          <button
            type="button"
            onClick={handleAddSelected}
            disabled={selectedTags.size === 0 || isLoading}
            className="btn-primary"
          >
            {t("recruiting:tags.addSelected", { count: selectedTags.size })}
          </button>
        </div>
      </dialog>
    </div>,
    document.body,
  );
};

export default TagSuggestionsModal;
